import { SensorType } from "constant/gateway";
import { ChartAttribute } from "generated/models";
import { cloneDeep, get, isEmpty, orderBy, sumBy } from "lodash";
import moment, { Moment, unitOfTime } from "moment";
import {
    BarChartSeries,
    ConsumptionChartBy,
    ConsumptionChartData,
} from "types";
import { SensorDetails } from "types/Sensors";
import {
    addDecimalPlace,
    combineResultArrays,
    convertPrometheusTimeToSeconds,
    convertSecondsToPrometheusTime,
    getYaxisDetails,
    monthsToSeconds,
    totalDoesNotExceedMax,
} from "utils/functions";
import { getSensorType } from "utils/sensorFunctions";
import { defaultChartRefreshRate } from "constant";
import WebsocketQuery from "service/websocketQuery";
import LocalStorageService from "service/localStorageService";
import { DISABLE_MISSING_DATA } from "utils/config";

type MetricType = "waterflow" | "energy";

type MetricValue = [number, string];

type MetricData = Pick<ChartAttribute, "device_id" | "said"> & {
    values: MetricValue[];
};

type QueryResult = {
    metric: {
        uid: string;
        said: string;
    };
    values: MetricValue[];
};

type MetricValueFilter = {
    /** The data's timestamp must be greater than or equal to this value */
    onOrAfter: Moment;

    /** The data's timestamp must be less than this value */
    before?: Moment;

    /** The data's timestamp must be less than or equal to this value */
    onOrBefore?: Moment;
};

type MetricDataForComparison = {
    metricData: MetricData[];
    previousMetricData: MetricData[];
};

/**
 * Get browser's timezone offset in hours
 * Uses Date.getTimezoneOffset() which is universally supported
 *
 * Examples:
 * - Vietnam (UTC+7): getTimezoneOffset() returns 7
 * - US Eastern (UTC-5): getTimezoneOffset() returns -5
 * - UK (UTC+0): getTimezoneOffset() returns 0
 * - Japan (UTC+9): getTimezoneOffset() returns 9
 * - India (UTC+5:30): getTimezoneOffset() returns 5.5
 * - Nepal (UTC+5:45): getTimezoneOffset() returns 5.75
 */
const getTimezoneOffset = (): number => {
    try {
        const offsetMinutes = new Date().getTimezoneOffset();
        const offsetHours = -offsetMinutes / 60; // Convert to hours and invert sign
        return offsetHours;
    } catch (error) {
        console.warn(
            "Failed to detect timezone, falling back to UTC+7:",
            error
        );
        return 7; // Fallback to UTC+7
    }
};

const WS = new WebsocketQuery();

/**
 * Same query for all ranges - using consumption metrics for WebSocket queries
 */
const CombinedConsumptionQuery: Record<MetricType, string> = {
    waterflow: "mqtt2tsdb_flowvolume_m3",
    energy: "mqtt2tsdb_activeenergy_kwh",
};

// Pre-defined query metrics for combined chart
type CombinedChartRawQuery = "mqtt2tsdb_gauge_na";
enum CombinedChartResolutionQuery {
    "1m" = "avg_1m_gauge_na",
    "5m" = "avg_5m_gauge_na",
    "30m" = "avg_30m_gauge_na",
    "1h" = "avg_1h_gauge_na",
}
type AggregationMetric = keyof typeof CombinedChartResolutionQuery;
type CombinedChartQuery = CombinedChartRawQuery | CombinedChartResolutionQuery;
const DefaultCombinedChartRawQuery: CombinedChartRawQuery =
    "mqtt2tsdb_gauge_na";

const timeUnitMapping: Record<ConsumptionChartBy, moment.unitOfTime.Base> = {
    [ConsumptionChartBy.Day]: "day",
    [ConsumptionChartBy.Week]: "week",
    [ConsumptionChartBy.Month]: "month",
    [ConsumptionChartBy.Year]: "year",
};

/**
 * Convert moment unitOfTime.Base back to ConsumptionChartBy
 */
const getConsumptionChartByFromTimeUnit = (
    timeUnit: moment.unitOfTime.Base
): ConsumptionChartBy => {
    switch (timeUnit) {
        case "day":
            return ConsumptionChartBy.Day;
        case "week":
            return ConsumptionChartBy.Week;
        case "month":
            return ConsumptionChartBy.Month;
        case "year":
            return ConsumptionChartBy.Year;
        default:
            return ConsumptionChartBy.Day; // fallback
    }
};

/**
 * Check if chart period requires timezone offset for proper data alignment
 */
const requiresTimezoneOffset = (groupBy: ConsumptionChartBy): boolean => {
    return (
        groupBy === ConsumptionChartBy.Week ||
        groupBy === ConsumptionChartBy.Month ||
        groupBy === ConsumptionChartBy.Year
    );
};

const getCurrentAndPreviousTime = (
    queryTime: number,
    timeUnit: unitOfTime.Base
) => {
    const time = moment.unix(queryTime);
    const previousTime = moment(time).subtract(1, timeUnit);
    return {
        time,
        previousTime,
    };
};

const CHART_TYPE_SINGLE = "SINGLE";

const consumptionQuery = (query: string) =>
    query
        .replace(/mqtt2tsdb_activepower_kw/, "mqtt2tsdb_activeenergy_kwh")
        .replace(/mqtt2tsdb_flowrate_m3ph/, "mqtt2tsdb_flowvolume_m3");

const changeDataTo2DP = (data: MetricData, chart_type: string): MetricData => {
    if (!data?.values || !Array.isArray(data.values)) {
        return data;
    }

    // Create a deep copy to avoid mutating the input
    const transformedData: MetricData = {
        ...data,
        values: data.values.map((valueEntry: MetricValue): MetricValue => {
            const [timestamp, value] = valueEntry;
            const newTimestamp =
                chart_type === CHART_TYPE_SINGLE ? timestamp * 1000 : timestamp;
            const numericValue = Number.parseFloat(value);
            const formattedValue = isNaN(numericValue)
                ? "0.00"
                : numericValue.toFixed(2);

            return [newTimestamp, formattedValue];
        }),
    };

    return transformedData;
};

const _chartMissingOption = {
    type: "line",
    itemStyle: {
        color: "background: rgba(243, 199, 173, 0.05)",
    },
    lineStyle: {
        color: "rgba(251, 108, 89, 1)",
        type: "dashed",
    },
    smooth: true,
    showSymbol: false,
    areaStyle: {
        origin: "start",
    },
    markLine: {
        symbol: ["none", "none"],
        lineStyle: {
            color: "#ECA48E",
            type: "dashed",
        },
        label: {
            show: false,
        },
        data: [
            {
                xAxis: 1652672793000,
            },
            {
                xAxis: 1652672862000,
            },
        ],
    },
};

const splitSeriesMissing = ({
    result,
    gapTime,
    chartOptions,
    isBinaryChart,
}: {
    result: any[];
    gapTime: number;
    chartOptions: any;
    isBinaryChart: boolean;
}) => {
    const _result = cloneDeep(result);

    const combinedResult: any = {
        metric: {},
        values: [],
    };
    _result.forEach((r) => {
        combinedResult.metric = r.metric;
        const mapped = r.values.map((v: any) => [v[0] * 1000, v[1]]);
        combinedResult.values.push(...mapped);
    });

    const dataArray = orderBy(combinedResult.values, ["[0]"], ["asc"]);

    let seriesGroup = [];
    let seriesGroupItem = [];
    let tailIndex = 0;
    let headIndex = 1;
    const interval = gapTime / 1000;
    const allowanceMargin = 1.5;

    for (tailIndex; tailIndex < dataArray.length; tailIndex++) {
        const difference =
            (dataArray[headIndex]?.[0] - dataArray[tailIndex]?.[0]) / 1000;
        let isMissing = false;
        isMissing =
            dataArray.length > 1 && difference > interval * allowanceMargin;

        if (isMissing) {
            if (seriesGroup.length === 0) {
                // first missing
                seriesGroupItem.push(dataArray[tailIndex]);
                seriesGroup.push({
                    label: "missing",
                    data: seriesGroupItem,
                    ...(isBinaryChart && { step: "end" }),
                    ..._chartMissingOption,
                });
            } else {
                // subsequent missing
                const currentLabel = seriesGroup[seriesGroup.length - 1].label;
                if (currentLabel === "missing") {
                    // if latest label is missing, continue push
                    seriesGroupItem.push(dataArray[tailIndex]);
                } else {
                    // if latest label is not missing, create new missing group
                    seriesGroup[seriesGroup.length - 1].data.push(
                        dataArray[tailIndex]
                    );
                    seriesGroupItem = [];
                    seriesGroupItem.push(dataArray[tailIndex]);
                    seriesGroup.push({
                        label: "missing",
                        data: seriesGroupItem,
                        ...(isBinaryChart && { step: "end" }),
                        ..._chartMissingOption,
                    });
                }
            }
        } else if (seriesGroup.length === 0) {
            // first non-missing
            seriesGroupItem.push(dataArray[tailIndex]);
            seriesGroup.push({
                data: seriesGroupItem,
                label: "not-missing",
                ...(isBinaryChart && { step: "end" }),
                ...chartOptions,
            });
        } else {
            // subsequent non-missing
            const currentLabel = seriesGroup[seriesGroup.length - 1].label;
            if (currentLabel === "not-missing") {
                // if latest label is not missing, continue push
                seriesGroupItem.push(dataArray[tailIndex]);
            } else {
                // if latest label is missing, create new not-missing group
                seriesGroup[seriesGroup.length - 1].data.push(
                    dataArray[tailIndex]
                );
                seriesGroupItem = [];
                seriesGroupItem.push(dataArray[tailIndex]);
                seriesGroup.push({
                    data: seriesGroupItem,
                    label: "not-missing",
                    ...(isBinaryChart && { step: "end" }),
                    ...chartOptions,
                });
            }
        }

        headIndex++;
    }

    return {
        names: [combinedResult?.metric?.name],
        series: seriesGroup,
    };
};

const _getSampleQuery = ({
    response,
    gapTime,
    chartColor,
    prepareAggregated,
    minResponse,
    maxResponse,
    name,
}: any) => {
    const funcRet: {
        info: any;
        names: any;
        series: any;
        aggregatedSeries: any;
    } = {
        info: get(response, "data.data.result[0].metric", {}),
        names: [],
        series: [],
        aggregatedSeries: undefined,
    };

    const currentChartOption = {
        type: "line",
        smooth: true,
        showSymbol: false,
        itemStyle: {
            color: chartColor,
        },
    };

    const deviceInfo = funcRet.info;
    const { isBinary } = getYaxisDetails({
        appId: deviceInfo.app,
        object: deviceInfo.object,
        said: deviceInfo.said,
    });
    const { names: seriesGroupNames, series: seriesGroup } = splitSeriesMissing(
        {
            result: get(response, "data.data.result", []),
            gapTime,
            chartOptions: currentChartOption,
            isBinaryChart: isBinary,
        }
    );

    if (prepareAggregated) {
        if (
            !get(response, "data.data.result[0]", {}) ||
            !get(minResponse, "data.data.result[0]", {}) ||
            !get(maxResponse, "data.data.result[0]", {})
        ) {
            return {
                info: {},
                names: [""],
                series: [],
            };
        }

        // BE may return multiple data arrays. Combine them and remove duplicate timestamps
        combineResultArrays(response);
        combineResultArrays(minResponse);
        combineResultArrays(maxResponse);

        const results = changeDataTo2DP(
            response.data.data.result[0],
            CHART_TYPE_SINGLE
        );

        const minResults = changeDataTo2DP(
            minResponse.data.data.result[0],
            CHART_TYPE_SINGLE
        );
        const maxResults = changeDataTo2DP(
            maxResponse.data.data.result[0],
            CHART_TYPE_SINGLE
        );
        const series: any = [];
        const resBody = {
            symbolSize: 15,
            color: chartColor,
            type: "scatter",
            name,
            data: results.values,
        };

        const maxBody = {
            symbolSize: 7,
            color: "#9BD88C",
            type: "scatter",
            name: "Max",
            data: maxResults.values,
        };
        const minBody = {
            symbolSize: 7,
            color: "#ED8787",
            type: "scatter",
            name: "Min",
            data: minResults.values,
        };
        const lineDataList = (
            readinglst: any,
            maxReadinglst: any,
            minReadingLst: any
        ) => {
            const body = [];
            for (let i = 0; i < readinglst.values.length; i++) {
                body.push(cloneDeep(readinglst.values[i]));
                body.push(maxReadinglst.values[i]);
                body.push(minReadingLst.values[i]);
            }

            for (let i = 0; i < body.length; i = i + 3) {
                body[i][1] = NaN;
            }

            return body;
        };
        const lineBody = {
            color: "#BECAE3",
            type: "line",
            zlevel: -1,
            data: lineDataList(results, maxResults, minResults),
        };
        series.push(resBody, maxBody, minBody, lineBody);
        funcRet.names = [get(results, "metric.name", ""), "Max", "Min"];
        funcRet.aggregatedSeries = series;
        funcRet.series = seriesGroup;
    }

    funcRet.names = seriesGroupNames;
    funcRet.series = seriesGroup;

    return funcRet;
};

const getAggregateBy = ({
    timeValue,
    numberOfChart,
}: {
    timeValue: number;
    numberOfChart: number;
}): AggregationMetric => {
    // Requirement: if > 6mths, use hourly aggregation
    if (timeValue > monthsToSeconds(6)) {
        return "1h";
    }

    const aggregationInArray: AggregationMetric[] = ["1m", "5m", "30m", "1h"];

    for (let aggregation of aggregationInArray) {
        const totalAggregatedSample = Math.floor(
            timeValue / convertPrometheusTimeToSeconds(aggregation)
        );

        if (
            totalDoesNotExceedMax({
                total: totalAggregatedSample,
                numberOfChart: numberOfChart,
            })
        ) {
            return aggregation;
        }
    }

    // Fallback to default aggregation if no suitable one is found, return 1h is more safe. This is not expected to be reached.
    return "1h";
};

const roundTimestamp = (date: Date | number, seconds: number) => {
    if (typeof date === "number") {
        date = new Date(date);
    }

    const roundedDate = new Date(
        Math.ceil(date.getTime() / (seconds * 1000)) * seconds * 1000
    );

    return Math.floor(roundedDate.getTime() / 1000);
};

const fetchMetricData = async (params: any = {}) => {
    const orgId = LocalStorageService.getItem("orgId");

    if (!WS.isReady(orgId)) {
        await WS.initConnection(orgId);
    }

    let payload = params;
    if (typeof params === "string") {
        // Url encode the params if it's a string
        const url = new URL(params);
        payload = Object.fromEntries(url.searchParams.entries());
    }

    // Remove some unnecessary properties from payload to prevent sending them over WebSocket
    delete payload.token;
    delete payload.originQuery;

    const response = await WS.fetch(payload);

    return {
        data: {
            data: {
                result: response.data,
            },
        },
    };
};

const getMQTTquery: any = ({
    queryType,
    query,
    time_range,
    lastTimestamp,
    timeValue,
    aggregationType,
    numberOfChart,
    isCombinedChart = false,
    sensorReportRate,
}: {
    queryType: "raw" | "aggregated";
    query: string;
    time_range: string;
    lastTimestamp: string;
    timeValue: number;
    aggregationType?: "avg" | "min" | "max";
    numberOfChart: number;
    isCombinedChart?: boolean;
    sensorReportRate?: number;
}) => {
    if (isCombinedChart) {
        let queryMetric: CombinedChartQuery = DefaultCombinedChartRawQuery;
        let resolution: string = convertSecondsToPrometheusTime(
            sensorReportRate ?? defaultChartRefreshRate
        );

        if (queryType === "aggregated") {
            const aggregatedResolution = getAggregateBy({
                timeValue,
                numberOfChart,
            });
            resolution = aggregatedResolution;
            queryMetric = CombinedChartResolutionQuery[aggregatedResolution];

            // Special cases, required from the backend, resolutions same as the metrics will not work
            if (
                [
                    CombinedChartResolutionQuery["30m"],
                    CombinedChartResolutionQuery["1h"],
                ].includes(queryMetric)
            ) {
                resolution = "5m";
            }
        }

        const resolutionInSeconds = convertPrometheusTimeToSeconds(resolution);
        const roundedTimestamp = roundTimestamp(
            new Date(Number(lastTimestamp) * 1000),
            resolutionInSeconds
        );

        return {
            body: {
                time_series: queryMetric,
                time_range,
                time: roundedTimestamp,
                resolution,
                post_op: "SUM",
            },
        };
    }

    if (queryType === "raw") {
        return {
            body: {
                query: `${query}[${time_range}]`,
                time: lastTimestamp,
            },
        };
    } else if (queryType === "aggregated") {
        const customQuery = query.replace("mqtt2tsdb", "");
        const aggregationBy = getAggregateBy({
            timeValue,
            numberOfChart,
        });

        return {
            body: {
                query: `${aggregationType}_${aggregationBy}${customQuery}[${time_range}]`,
                time: lastTimestamp,
            },
        };
    }
};

const groupDataByTime = (
    groupBy: ConsumptionChartBy,
    data: any[],
    lastTime: Moment
): {
    categories: string[] | number[];
    data: any[];
} => {
    switch (groupBy) {
        case ConsumptionChartBy.Week:
            return groupDataByWeek(data);
        case ConsumptionChartBy.Month:
            return groupDataByMonth(data, lastTime);
        case ConsumptionChartBy.Year:
            return groupDataByYear(data);
        default:
            return groupDataByHour(data);
    }
};

export const groupDataByHour = (data: any[]) => {
    const pad = (num: number) => String(num).padStart(2, "0");
    const hours = Array.from(Array(24).keys()).map((i: number, index) =>
        pad(i)
    );
    const groupData = isEmpty(data)
        ? []
        : hours.reduce((acc, hh) => {
              const hourData = data.filter((t: any) => {
                  const endTime = moment.unix(t?.[0]);
                  const thh = endTime.hours();

                  return thh === +hh;
              });

              const groupValue = isEmpty(hourData)
                  ? undefined
                  : sumBy(hourData, (t) => Number(t[1]));

              acc.push(groupValue);
              return acc;
          }, [] as any[]);

    return {
        categories: hours.map((h) => moment(h, "HH").format("hA")),
        data: groupData,
    };
};

export const groupDataByWeek = (data: any[]) => {
    const days = Array.from(Array(7).keys()).map((i: number, index) => i);
    const groupData = isEmpty(data)
        ? []
        : days.reduce((acc, day) => {
              const dayData = data.filter((t: any) => {
                  const endTime = moment.unix(t?.[0]);

                  const tDay = endTime.day();
                  return tDay === day;
              });

              // Check empty data to prevent the sumBy from returning 0
              const groupValue = isEmpty(dayData)
                  ? undefined
                  : sumBy(dayData, (t) => Number(t[1]));

              acc.push(groupValue);
              return acc;
          }, [] as any[]);

    return {
        categories: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        data: groupData,
    };
};

export const groupDataByMonth = (data: any[], lastTime: Moment) => {
    const daysInMonth = lastTime.daysInMonth();
    const days = Array.from(Array(daysInMonth).keys()).map(
        (i: number, index) => i + 1
    );
    const groupData = isEmpty(data)
        ? []
        : days.reduce((acc, date) => {
              const monthData = data.filter((t: any) => {
                  const endTime = moment.unix(t?.[0]);

                  const tDate = endTime.date();
                  return tDate === date;
              });

              // Check empty data to prevent the sumBy from returning 0
              const groupValue = isEmpty(monthData)
                  ? undefined
                  : sumBy(monthData, (t) => Number(t[1]));

              acc.push(groupValue);
              return acc;
          }, [] as any[]);

    return {
        categories: days,
        data: groupData,
    };
};

export const groupDataByYear = (data: any[]) => {
    const months = Array.from(Array(12).keys()).map(
        (i: number, index) => i + 1
    );

    const groupData = isEmpty(data)
        ? []
        : months.reduce((acc, month) => {
              const yearData = data.filter((t: any) => {
                  const endTime = moment.unix(t?.[0]);

                  const tMonth = endTime.format("MM");
                  return +tMonth === month;
              });

              // Check empty data to prevent the sumBy from returning 0
              const groupValue = isEmpty(yearData)
                  ? undefined
                  : sumBy(yearData, (t) => Number(t[1]));

              acc.push(groupValue);
              return acc;
          }, [] as any[]);

    return {
        categories: months.map((m) => moment(m, "M").format("MMM")),
        data: groupData,
    };
};

const calculateTotal = (metricData: MetricData[]): number | null => {
    if (isEmpty(metricData)) {
        return null;
    }

    return sumBy(metricData, (data: MetricData) => {
        const values = data.values || [];
        return sumBy(values, (valueEntry: MetricValue) => {
            const numericValue = Number(valueEntry[1]);
            return isNaN(numericValue) ? 0 : numericValue;
        });
    });
};

const generateChartSeries = ({
    sensorDetails,
    groupBy,
    metricData,
    lastTime,
}: {
    sensorDetails: SensorDetails[];
    groupBy: ConsumptionChartBy;
    metricData: MetricData[];
    lastTime: Moment;
}) => {
    let categories: string[] | number[] = [];
    const series = sensorDetails.map<BarChartSeries>((details) => {
        const rawData = metricData.find(
            (data: MetricData) =>
                data.device_id === details.device_id &&
                data.said === details.said
        );

        let data = [];

        if (rawData) {
            const groupData = groupDataByTime(
                groupBy,
                get(rawData, "values", []),
                lastTime
            );
            data = groupData.data;

            if (!categories?.length) {
                categories = groupData.categories;
            }
        }

        return {
            id: `${details.device_id ?? ""}_${details.said ?? 0}`,
            data,
            type: "bar",
            barWidth: "60%",
            name: details.name,
            color: details.color,
            stack: "Consumption",
        };
    });

    return {
        series,
        categories,
    };
};

export const buildPromQL = ({
    fn, // e.g. "sum", "avg"
    metric, // e.g. "mqtt2tsdb_gauge_na"
    selectors, // array of label objects: [ { gateway: "X" }, { gateway: "Y" } ]
    range, // e.g. "30m"
    resolution, // e.g. "5s"
}: {
    fn?: string;
    metric: string;
    selectors: Array<{ [key: string]: string | number }>;
    range?: string;
    resolution?: string;
}) => {
    // convert labels to PromQL {key="value"} form
    const toLabelStr = (labels: { [key: string]: string | number }) => {
        const str = Object.entries(labels)
            .map(([k, v]) => `${k}="${String(v)}"`)
            .join(",");
        return `{${str}}`;
    };

    // join multiple selectors with " or "
    const innerExpr = selectors
        .map((lbl) => `${metric}${toLabelStr(lbl)}`)
        .join(" or ");

    // wrap with function
    let expr = fn ? `${fn}(${innerExpr})` : innerExpr;

    // append [range:resolution] or [range]
    if (range) {
        expr += resolution ? `[${range}:${resolution}]` : `[${range}]`;
    }

    return expr;
};

const getCombinedMetricData = async ({
    query,
    time_range,
    gatewayIds,
    isRawData,
    lastTimestamp,
    timeValue,
    sensorReportRate,
}: {
    query: string;
    chartColor: any;
    sensorType: string;
    time_range: string;
    query_time: string | number | null;
    name: string;
    gatewayIds?: string[];
    isRawData: boolean;
    lastTimestamp: string | number;
    timeValue: number;
    sensorReportRate: number;
}) => {
    const mttqQuery = getMQTTquery({
        queryType: isRawData ? "raw" : "aggregated",
        query,
        time_range,
        lastTimestamp,
        timeValue,
        numberOfChart: 1,
        isCombinedChart: true,
        sensorReportRate,
    });

    const queryData = mttqQuery.body;
    const params: any = {
        time: queryData.time,
    };

    if (gatewayIds?.length) {
        params.query = buildPromQL({
            fn: "sum",
            metric: queryData.time_series,
            selectors: gatewayIds.map((gatewayId: string) => ({
                gateway: gatewayId,
            })),
            range: queryData.time_range,
            resolution: queryData.resolution,
        });
    }

    return fetchMetricData(params);
};

export const getSampleQuery = async ({
    query,
    chartColor,
    sensorType,
    gapTime,
    time_range,
    query_time,
    name,
    isCombinedChart = false,
    gatewayIds,
}: {
    query: string;
    chartColor: any;
    sensorType: string;
    gapTime: number;
    time_range: string;
    query_time: string | number | null;
    name: string;
    isCombinedChart?: boolean;
    gatewayIds?: string[];
}) => {
    let response: any;
    let minResponse: any;
    let maxResponse: any;

    const lastTimestamp = query_time || moment().unix();
    const timeValue = convertPrometheusTimeToSeconds(time_range);
    const sensorReportRate: number = gapTime / 1000;
    const totalSample = Math.floor(timeValue / sensorReportRate);

    const isRawData =
        totalDoesNotExceedMax({ total: totalSample, numberOfChart: 1 }) &&
        timeValue <= monthsToSeconds(6);

    let prepareAggregated = false;
    if (isCombinedChart) {
        response = await getCombinedMetricData({
            query,
            chartColor,
            sensorType,
            time_range,
            query_time,
            name,
            gatewayIds,
            isRawData,
            lastTimestamp,
            timeValue,
            sensorReportRate,
        });

        gapTime =
            convertPrometheusTimeToSeconds(
                getAggregateBy({ timeValue, numberOfChart: 1 })
            ) * 1000;
    } else if (isRawData) {
        try {
            const mttqQuery = getMQTTquery({
                queryType: "raw",
                query,
                time_range,
                lastTimestamp,
                timeValue,
                numberOfChart: 1,
                isCombinedChart,
            });

            response = await fetchMetricData(mttqQuery.body);
        } catch (err) {
            // TO BE ADDED: Handle error obj
            return err;
        }
    } else {
        prepareAggregated = true;
        gapTime =
            convertPrometheusTimeToSeconds(
                getAggregateBy({ timeValue, numberOfChart: 1 })
            ) * 1000;

        [response, minResponse, maxResponse] = await Promise.all(
            ["avg", "min", "max"].map((type) => {
                const mttqQuery = getMQTTquery({
                    queryType: "aggregated",
                    query,
                    time_range,
                    lastTimestamp,
                    timeValue,
                    aggregationType: type,
                    numberOfChart: 1,
                    isCombinedChart,
                });

                return fetchMetricData(mttqQuery.body);
            })
        );
    }

    return _getSampleQuery({
        response,
        gapTime,
        chartColor,
        timeValue,
        prepareAggregated,
        minResponse,
        maxResponse,
        sensorType,
        name,
    });
};

const _getStackedChartQuery = ({
    panelQueries,
    time_range,
    query_time,
    gapTime,
}: {
    panelQueries: string[];
    time_range: string;
    query_time: string | number | null;
    gapTime: number;
}) => {
    const lastTimestamp = query_time || moment().unix();
    const timeValue = convertPrometheusTimeToSeconds(time_range);

    const sensorReportRate: number = gapTime / 1000;
    const totalSample = Math.floor(timeValue / sensorReportRate);

    const queries: any = [];
    for (let i = 0; i < panelQueries.length; i++) {
        if (
            totalDoesNotExceedMax({
                total: totalSample,
                numberOfChart: panelQueries.length,
            })
        ) {
            const mttqQuery = getMQTTquery({
                queryType: "raw",
                query: panelQueries[i],
                time_range,
                lastTimestamp,
                timeValue,
                numberOfChart: panelQueries.length,
            });
            queries.push(mttqQuery.body);
        } else {
            const mttqQuery = getMQTTquery({
                queryType: "aggregated",
                query: panelQueries[i],
                time_range,
                lastTimestamp,
                timeValue,
                aggregationType: "avg",
                numberOfChart: panelQueries.length,
            });
            queries.push(mttqQuery.body);
        }
    }

    return queries;
};

export const getStackedChartQuery = async ({
    panels,
    namesAry,
    yAxisAry,
    time_range,
    query_time,
    gapTime,
}: {
    panels: any;
    namesAry: any;
    yAxisAry: any;
    time_range: string;
    query_time: string | number | null;
    gapTime: number;
}) => {
    const timeValue = convertPrometheusTimeToSeconds(time_range);
    const sensorReportRate: number = gapTime / 1000;
    const totalSample = Math.floor(timeValue / sensorReportRate);

    const newQueries: any = _getStackedChartQuery({
        panelQueries: panels.queries,
        time_range,
        query_time,
        gapTime,
    });

    let queryRes: any = [];
    try {
        queryRes = await Promise.all(
            newQueries.map((i: any) => fetchMetricData(i))
        );
    } catch (err) {
        return err;
    }

    let series: any = [];
    for (let h = 0; h < queryRes.length; h++) {
        const isRawData = totalDoesNotExceedMax({
            total: totalSample,
            numberOfChart: queryRes.length,
        });

        let result: any;
        if (isRawData) {
            result = queryRes[h].data.data.result;
        } else {
            combineResultArrays(queryRes[h]);
            result = [
                changeDataTo2DP(queryRes[h].data.data.result[0], "STACKED"),
            ];
        }

        const thisIndex = yAxisAry.findIndex(
            (info: any) => info.sensorType === panels.attributes[h]?.name
        );

        const isBinary = panels.attributes[h]?.isBinary || false;

        if (isRawData && !DISABLE_MISSING_DATA) {
            const currentChartOption = {
                symbolSize: 15,
                color: panels.attributes[h]?.color,
                type: "line",
                name: namesAry[h] ? `${namesAry[h]}` : "",
                yAxisIndex: thisIndex !== -1 ? thisIndex : 0,
                lineStyle: {
                    type: thisIndex === 0 ? "solid" : [10, 2],
                    color: panels.attributes[h]?.color,
                },
                smooth: true,
                showSymbol: false,
            };

            const { series: seriesGroup } = splitSeriesMissing({
                result,
                gapTime,
                chartOptions: currentChartOption,
                isBinaryChart: isBinary,
            });

            seriesGroup.forEach((s: any) => {
                s.info = get(queryRes[h], `data.data.result[0].metric`, {});
                s.yAxisIndex = thisIndex !== -1 ? thisIndex : 0;
            });

            series = [...series, ...seriesGroup];
        } else {
            let dataResult = [];
            for (const element of result) {
                for (const value of element.values) {
                    value[0] = value[0] * 1000;
                }
                dataResult = element.values;
            }

            const sample = {
                name: namesAry[h] ? `${namesAry[h]}` : "",
                type: "line",
                itemStyle: {
                    color: panels.attributes[h]?.color,
                },
                yAxisIndex: thisIndex !== -1 ? thisIndex : 0,
                lineStyle: {
                    type: thisIndex === 0 ? "solid" : [10, 2],
                },
                data: dataResult,
                smooth: true,
                showSymbol: false,
                info: get(queryRes[h], `data.data.result[0].metric`, {}),
            };

            series = [...series, sample];
        }
    }

    return { series };
};

export const getOnlineOfflineValue = async (
    gateway_id: string,
    timeValue: string
) => {
    try {
        const query = `sum(count_over_time(mqtt2tsdb_heartbeat_na{gateway="${gateway_id}"}[${timeValue}]))`;
        return fetchMetricData({ query });
    } catch (err) {
        return err;
    }
};

type ConsumptionMetricInput = {
    sensorDetails: SensorDetails[];
    queryTime: number;
    groupBy: ConsumptionChartBy;
    sensors: any[];
    queries: string[];
};

const metricDataFromQueryResults = (
    results: QueryResult[],
    filter: MetricValueFilter,
    groupBy?: ConsumptionChartBy
): MetricData[] => {
    const isValidTimestamp = (timestamp: number): boolean => {
        if (timestamp < filter.onOrAfter.unix()) {
            return false;
        }

        if (filter.before && timestamp >= filter.before.unix()) {
            return false;
        }

        if (filter.onOrBefore && timestamp > filter.onOrBefore.unix()) {
            return false;
        }

        return true;
    };

    return results.map((result) => {
        const device_id = get(result, "metric.uid").toString();
        const said = Number.parseInt(get(result, "metric.said"));

        /**
         * Apply time offset for consumption charts based on groupBy period:
         *
         * DAILY: Subtract 1h only
         * - Backend data points are hourly aggregates with end timestamps
         * - Subtract 1h to align with chart display
         *
         * WEEKLY/MONTHLY/YEARLY: Subtract 24h + timezone offset
         * - increase(24h) returns end time of 24h period (next day at timezone offset time)
         * - Subtract 24h (period duration) + timezone offset = proper alignment
         * - Converts "31st July 7am" → "30th July 12am" for proper day grouping
         *
         * Ex: Backend [31st July 7am, value] → Display [30th July, value]
         */
        result.values = result.values.map((value) => {
            const timezoneOffset = getTimezoneOffset();
            const hoursToSubtract =
                groupBy === ConsumptionChartBy.Day || !groupBy
                    ? 1
                    : 24 + timezoneOffset;
            const originalTimestamp = value[0];
            const timestamp = moment
                .unix(originalTimestamp)
                .subtract(hoursToSubtract, "h")
                .unix();

            return [timestamp, value[1]];
        });

        const values = (result.values || []).filter((value) =>
            isValidTimestamp(value[0])
        );

        return {
            values,
            device_id,
            said,
        };
    });
};

const getSingleConsumptionMetricData = async ({
    sensorDetails,
    groupBy,
    queryTime,
    query,
}: {
    sensorDetails: SensorDetails;
    groupBy: ConsumptionChartBy;
    queryTime: number;
    query: string;
}): Promise<MetricDataForComparison> => {
    const timeUnit = timeUnitMapping[groupBy];

    const { time, previousTime } = getCurrentAndPreviousTime(
        queryTime,
        timeUnit
    );

    const getMetricData = async (time: Moment): Promise<MetricData[]> => {
        const { start, end } = _getConsumptionQueryTime(time, timeUnit);

        // Define consumption query configuration based on time period
        const queryConfig = {
            [ConsumptionChartBy.Day]: { interval: "1h", range: "[24h:1h]" },
            [ConsumptionChartBy.Week]: { interval: "1d", range: "[7d:1d]" },
            [ConsumptionChartBy.Month]: {
                interval: "1d",
                range: `[${time.daysInMonth()}d:1d]`,
            },
            [ConsumptionChartBy.Year]: {
                interval: "1d",
                range: `[${moment(time).endOf("year").dayOfYear()}d:1d]`,
            },
        };

        const config =
            queryConfig[groupBy] || queryConfig[ConsumptionChartBy.Day];

        const timezoneOffset = getTimezoneOffset();
        const offsetString = requiresTimezoneOffset(groupBy)
            ? ` offset ${timezoneOffset}h`
            : "";
        const wsQuery = `delta(${consumptionQuery(query)}[${
            config.interval
        }]${offsetString})${config.range}`;

        const queryTime = requiresTimezoneOffset(groupBy)
            ? getCombinedConsumptionQueryTime(time.unix(), timeUnit)
            : end.unix();

        const params = {
            query: wsQuery,
            time: queryTime,
            originQuery: query,
        };

        const response = await fetchMetricData(params);

        const transformedResults = metricDataFromQueryResults(
            get(response, "data.data.result", []),
            { onOrAfter: start, onOrBefore: end },
            groupBy
        ).filter(
            (metricData) =>
                metricData.device_id === sensorDetails.device_id &&
                metricData.said === sensorDetails.said
        );

        return transformedResults;
    };

    const [metricData, previousMetricData] = await Promise.all([
        getMetricData(time),
        getMetricData(previousTime),
    ]);

    return { metricData, previousMetricData };
};

const _getConsumptionQueryTime = (
    endTime: Moment,
    timeUnit: unitOfTime.Base
) => {
    const endOfUnit = moment(endTime).endOf(timeUnit).toDate();
    const start = moment(endOfUnit).startOf(timeUnit);
    let end = moment(roundTimestamp(endOfUnit, 60 * 60) * 1000);

    const timeRange = convertSecondsToPrometheusTime(end.diff(start, "second"));

    return {
        start,
        end,
        timeRange,
    };
};

/**
 * Build WebSocket query for combined consumption charts
 */
const buildCombinedConsumptionWSQuery = (
    metricType: MetricType,
    sensorDetails: SensorDetails[],
    timeUnit: unitOfTime.Base,
    queryTime: number
): string => {
    const metricQuery = CombinedConsumptionQuery[metricType];
    const timezoneOffset = getTimezoneOffset();
    const time = moment.unix(queryTime);

    // Build individual sensor queries
    const sensorQueries = sensorDetails.map(
        ({ gateway_id, device_id, said }) => {
            return `delta(${metricQuery}{gateway="${gateway_id}", uid="${device_id}", said="${said}"}[${getIncreaseInterval(
                timeUnit
            )}]${getOffsetString(timeUnit, timezoneOffset)})`;
        }
    );

    // Combine queries with OR operator and add time range
    const combinedQuery = `(${sensorQueries.join(" or ")})${getTimeRange(
        timeUnit,
        time
    )}`;

    return combinedQuery;
};

/**
 * Get the delta interval based on time unit
 */
const getIncreaseInterval = (timeUnit: unitOfTime.Base): string => {
    switch (timeUnit) {
        case "day":
            return "1h";
        case "week":
        case "month":
        case "year":
            return "1d";
        default:
            return "1h";
    }
};

/**
 * Get the offset string for the query
 */
const getOffsetString = (
    timeUnit: unitOfTime.Base,
    vnOffset: number
): string => {
    switch (timeUnit) {
        case "day":
            return "";
        case "week":
        case "month":
        case "year":
            return ` offset ${vnOffset}h`;
        default:
            return "";
    }
};

/**
 * Get the time range for the query
 */
const getTimeRange = (timeUnit: unitOfTime.Base, time?: Moment): string => {
    switch (timeUnit) {
        case "day":
            return "[1d:1h]";
        case "week":
            return "[7d:1d]";
        case "month": {
            const daysInMonth = time ? time.daysInMonth() : 31;
            return `[${daysInMonth}d:1d]`;
        }
        case "year": {
            const daysInYear = time?.isLeapYear() ? 366 : 365;
            return `[${daysInYear}d:1d]`;
        }
        default:
            return "[1d:1h]";
    }
};

/**
 * Get the query time with proper timezone handling
 */
const getCombinedConsumptionQueryTime = (
    queryTime: number,
    timeUnit: unitOfTime.Base
): number => {
    const timezoneOffset = getTimezoneOffset();
    const time = moment.unix(queryTime);

    switch (timeUnit) {
        case "day":
            // For daily: use start of next day (00:00:00) instead of end of current day (23:59:59)
            return time.add(1, "day").startOf("day").unix();
        case "week":
            // For weekly: convert local time to UTC by adding timezone offset
            return time
                .endOf("week") // Saturday 23:59:59
                .startOf("day") // Saturday 00:00:00
                .add(1, "day") // Sunday 00:00:00 (next Sunday)
                .add(timezoneOffset, "hours") // Convert local time to UTC time
                .unix();
        case "month": {
            // For monthly: use start of NEXT month to capture complete current month data
            const startOfNextMonth = time
                .add(1, "month")
                .startOf("month")
                .startOf("day");
            return startOfNextMonth.add(timezoneOffset, "hours").unix();
        }
        case "year": {
            // For yearly: use start of NEXT year to capture complete current year data
            const startOfNextYear = time
                .add(1, "year")
                .startOf("year")
                .startOf("day");
            return startOfNextYear.add(timezoneOffset, "hours").unix();
        }
        default:
            return queryTime;
    }
};

const getMultipleConsumptionMetricData = async ({
    metricType,
    sensorDetails,
    queryTime,
    timeUnit,
}: {
    metricType: MetricType;
    sensorDetails: SensorDetails[];
    queryTime: number;
    timeUnit: unitOfTime.Base;
}): Promise<MetricDataForComparison> => {
    const { time, previousTime } = getCurrentAndPreviousTime(
        queryTime,
        timeUnit
    );

    const getMetricData = async (time: Moment): Promise<MetricData[]> => {
        const { start, end } = _getConsumptionQueryTime(time, timeUnit);

        const wsQuery = buildCombinedConsumptionWSQuery(
            metricType,
            sensorDetails,
            timeUnit,
            time.unix()
        );

        const wsQueryTime = getCombinedConsumptionQueryTime(
            time.unix(),
            timeUnit
        );

        const params = {
            query: wsQuery,
            time: wsQueryTime,
            originQuery: wsQuery,
        };

        const response = await fetchMetricData(params);

        const filteredResults = metricDataFromQueryResults(
            get(response, "data.data.result", []),
            { onOrAfter: start, onOrBefore: end },
            getConsumptionChartByFromTimeUnit(timeUnit)
        );

        return filteredResults;
    };

    const [metricData, previousMetricData] = await Promise.all([
        getMetricData(time),
        getMetricData(previousTime),
    ]);

    return {
        metricData,
        previousMetricData,
    };
};

/**
 * Fetches consumption data to be used in consumption charts.
 */
export const getConsumptionMetricV2 = async ({
    sensorDetails,
    queryTime,
    groupBy,
    queries,
    sensors,
}: ConsumptionMetricInput): Promise<ConsumptionChartData> => {
    const isSingle = sensorDetails?.length === 1;
    const lastTime = moment.unix(queryTime);

    const metricType =
        getSensorType(sensors[0]) === SensorType.WaterConsumption
            ? "waterflow"
            : "energy";
    const { metricData, previousMetricData } = isSingle
        ? await getSingleConsumptionMetricData({
              sensorDetails: sensorDetails[0],
              groupBy,
              queryTime,
              query: queries[0],
          })
        : await getMultipleConsumptionMetricData({
              metricType,
              sensorDetails,
              queryTime,
              timeUnit: timeUnitMapping[groupBy],
          });

    const total = calculateTotal(metricData);
    const previousTotal = calculateTotal(previousMetricData);

    const dayTotal =
        groupBy === ConsumptionChartBy.Day
            ? await getConsumptionChartTotal_ForDay(queries, queryTime)
            : total;

    const previousDayTotal =
        groupBy === ConsumptionChartBy.Day
            ? await getConsumptionChartTotal_ForDay(
                  queries,
                  moment.unix(queryTime).subtract(1, "day").unix()
              )
            : previousTotal;

    const { series, categories } = generateChartSeries({
        sensorDetails,
        metricData,
        lastTime,
        groupBy,
    });

    return {
        categories,
        previousTotal,
        series,
        total,
        dayTotal,
        previousDayTotal,
    };
};

export const getConsumptionChartTotal_ForDay = async (
    queries: string[],
    queryTime: number
): Promise<number | null> => {
    try {
        const time = queryTime ? moment.unix(queryTime) : moment();
        // Get local midnight for the current day
        const localMidnight = time.startOf("day").add(1, "day").unix();

        const wsQuery = queries
            .map((query) => `delta(${consumptionQuery(query)}[1d])`)
            .join(" or ");

        const params = {
            query: wsQuery,
            time: localMidnight,
        };

        const response = await fetchMetricData(params);
        const results = get(response, "data.data.result", []);

        if (results.length > 0) {
            const latestValue = results.reduce((sum: number, result: any) => {
                return sum + Number(addDecimalPlace(result.value[1], 4));
            }, 0);
            const numericValue = Number(latestValue);
            return isNaN(numericValue) ? null : numericValue;
        }

        return null;
    } catch (error) {
        console.error("Error fetching consumption total:", error);
        return null;
    }
};
